import React from 'react'
import type { TFunction } from 'i18next'
import type { TaxBurdenData, TaxComparison } from '@popety-io/types'
import { InfoTooltip } from '../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../TooltipMetricExplanation'
import { formatTaxBurden, calculatePercentageDifference } from '../taxService'
import {
  ComparisonCardsGrid,
  ComparisonProfileCard,
  ComparisonProfileTitle,
  ComparisonProfileValue,
  ComparisonProfileHeader,
  ComparisonBarsContainer,
  ComparisonBarRow,
  ComparisonBarLabel,
  ComparisonBarTrack,
  ComparisonBar,
  ComparisonBarValue,
  ComparisonDiffText,
} from '../TaxSection.styles'

export interface GeographicComparisonCardProps {
  data: TaxBurdenData
  comparison: TaxComparison
  t: TFunction
}

interface ProfileConfig {
  key: string
  locationValue: number
  cantonValue: number
  nationalValue: number
}

const getDiffColor = (diff: number): 'success' | 'danger' =>
  diff < 0 ? 'success' : 'danger'

const ProfileCard: React.FC<{
  profile: ProfileConfig
  locationName: string
  t: TFunction
}> = ({ profile, locationName, t }) => {
  const { locationValue, cantonValue, nationalValue } = profile
  const maxValue = Math.max(locationValue, cantonValue, nationalValue) * 1.1

  const cantonDiff = calculatePercentageDifference(locationValue, cantonValue)
  const nationalDiff = calculatePercentageDifference(
    locationValue,
    nationalValue
  )

  return (
    <ComparisonProfileCard>
      <ComparisonProfileHeader>
        <ComparisonProfileTitle>
          {t(`profiles.${profile.key}`)}
        </ComparisonProfileTitle>
        <InfoTooltip>
          <TooltipMetricExplanation
            title={t('tooltips.taxBurden.title')}
            shortDescription={t('tooltips.taxBurden.shortDescription')}
            calculation={t(`tooltips.taxBurden.calculation_${profile.key}`)}
            importance={t('tooltips.taxBurden.importance')}
            limitations={t(`tooltips.taxBurden.limitations_${profile.key}`)}
            source={t('tooltips.taxBurden.source')}
          />
        </InfoTooltip>
      </ComparisonProfileHeader>
      <ComparisonProfileValue>
        {formatTaxBurden(locationValue)}
      </ComparisonProfileValue>

      <ComparisonBarsContainer>
        {/* Location bar */}
        <ComparisonBarRow>
          <ComparisonBarLabel>{locationName}</ComparisonBarLabel>
          <ComparisonBarTrack>
            <ComparisonBar
              $width={(locationValue / maxValue) * 100}
              $color="neutral"
            />
          </ComparisonBarTrack>
          <ComparisonBarValue $color="neutral">
            {formatTaxBurden(locationValue)}
          </ComparisonBarValue>
        </ComparisonBarRow>

        {/* Canton bar */}
        <ComparisonBarRow>
          <ComparisonBarLabel>{t('comparison.canton')}</ComparisonBarLabel>
          <ComparisonBarTrack>
            <ComparisonBar
              $width={(cantonValue / maxValue) * 100}
              $color={getDiffColor(cantonDiff)}
            />
          </ComparisonBarTrack>
          <ComparisonBarValue $color={getDiffColor(cantonDiff)}>
            {formatTaxBurden(cantonValue)}
          </ComparisonBarValue>
        </ComparisonBarRow>

        {/* Canton diff text */}
        <ComparisonDiffText $positive={cantonDiff < 0}>
          {Math.abs(cantonDiff).toFixed(1)}%{' '}
          {cantonDiff < 0
            ? t('comparison.belowCantonAvg')
            : t('comparison.aboveCantonAvg')}
        </ComparisonDiffText>

        {/* National bar */}
        <ComparisonBarRow>
          <ComparisonBarLabel>{t('comparison.switzerland')}</ComparisonBarLabel>
          <ComparisonBarTrack>
            <ComparisonBar
              $width={(nationalValue / maxValue) * 100}
              $color={getDiffColor(nationalDiff)}
            />
          </ComparisonBarTrack>
          <ComparisonBarValue $color={getDiffColor(nationalDiff)}>
            {formatTaxBurden(nationalValue)}
          </ComparisonBarValue>
        </ComparisonBarRow>

        {/* National diff text */}
        <ComparisonDiffText $positive={nationalDiff < 0}>
          {Math.abs(nationalDiff).toFixed(1)}%{' '}
          {nationalDiff < 0
            ? t('comparison.belowNationalAvg')
            : t('comparison.aboveNationalAvg')}
        </ComparisonDiffText>
      </ComparisonBarsContainer>
    </ComparisonProfileCard>
  )
}

export const GeographicComparisonCard: React.FC<
  GeographicComparisonCardProps
> = ({ data, comparison, t }) => {
  const locationName = t('comparison.thisLocation')

  const profiles: ProfileConfig[] = [
    {
      key: 'single',
      locationValue: data.tax_burden_single,
      cantonValue: comparison.canton.tax_burden_single,
      nationalValue: comparison.national.tax_burden_single,
    },
    {
      key: 'married',
      locationValue: data.tax_burden_married,
      cantonValue: comparison.canton.tax_burden_married,
      nationalValue: comparison.national.tax_burden_married,
    },
    {
      key: 'family',
      locationValue: data.tax_burden_family,
      cantonValue: comparison.canton.tax_burden_family,
      nationalValue: comparison.national.tax_burden_family,
    },
  ]

  return (
    <ComparisonCardsGrid>
      {profiles.map((profile) => (
        <ProfileCard
          key={profile.key}
          profile={profile}
          locationName={locationName}
          t={t}
        />
      ))}
    </ComparisonCardsGrid>
  )
}
